import { createSlice } from '@reduxjs/toolkit';
import { PayeeApi, PayeeData } from '../services/payee';
import { CaseType, PayeeType } from '../type/enums';
import { CASES_WITH_RECOMMENDED_DETAILS, CASES_WITH_WRONG_TYPE } from '../constants/confirmationOfPayee';
import { is5xxError } from '../utils';

interface VerificationFailureInfo {
    reason_code: string | null;
    validationErrors: {
        name: boolean;
        type: boolean;
    };
}

interface PayeeState {
    unverifiedNewPayee: PayeeData | null;
    verificationFailureInfo: VerificationFailureInfo;
    payeeRecommendedDetails: PayeeData | null;
}

const initialState: PayeeState = {
    unverifiedNewPayee: null,
    verificationFailureInfo: {
        reason_code: null,
        validationErrors: {
            name: false,
            type: false,
        },
    },
    payeeRecommendedDetails: null,
};

const getNewType = (isWrongType: boolean, type: string) => {
    if (!isWrongType) return type;
    switch (type) {
        case PayeeType.BUSINESS:
            return PayeeType.PERSONAL;
        case PayeeType.PERSONAL:
            return PayeeType.BUSINESS;
        default:
            return '';
    }
};

const generateVerificationFailureInfo = (reason_code: string, recommendedName?: string) => ({
    reason_code,
    validationErrors: {
        name: Boolean(recommendedName) || reason_code === CaseType.ANNM,
        type: CASES_WITH_WRONG_TYPE.includes(reason_code),
    },
});

const generateRecommendedDetails = (
    reason_code: string,
    { type, accountNumber, sortCode, name }: any,
    recommendedName?: string
) => {
    if (!CASES_WITH_RECOMMENDED_DETAILS.includes(reason_code)) return null;
    const newType = getNewType(CASES_WITH_WRONG_TYPE.includes(reason_code), type);
    return {
        name: recommendedName ?? name,
        type: newType,
        accountNumber,
        sortCode,
    };
};

const handleVerifyPayeePending = (state, { meta: { arg } }) => {
    const { originalArgs } = arg;
    state.unverifiedNewPayee = originalArgs;
};

const handleVerifyPayeeFulfilled = (state, { payload }) => {
    const { reason_code, name: recommendedName } = payload;
    state.verificationFailureInfo = generateVerificationFailureInfo(reason_code, recommendedName);
    state.payeeRecommendedDetails = generateRecommendedDetails(reason_code, state.unverifiedNewPayee, recommendedName);
};

const handleVerifyPayeeRejected = (state, { payload: { status } }) => {
    if (is5xxError(status)) {
        state.verificationFailureInfo = generateVerificationFailureInfo(CaseType.E500);
    }
};

const payeeSlice = createSlice({
    name: 'payee',
    initialState,
    reducers: {
        resetPayeeState: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addMatcher(PayeeApi.endpoints.verifyPayee.matchPending, handleVerifyPayeePending);
        builder.addMatcher(PayeeApi.endpoints.verifyPayee.matchFulfilled, handleVerifyPayeeFulfilled);
        builder.addMatcher(PayeeApi.endpoints.verifyPayee.matchRejected, handleVerifyPayeeRejected);
    },
});

export const { resetPayeeState } = payeeSlice.actions;
export const selectUnverifiedNewPayee = (state) => state.payeeSlice.unverifiedNewPayee;
export const selectPayeeVerificationErrorInfo = (state) => state.payeeSlice.verificationFailureInfo;
export const selectPayeeRecommendedDetails = (state) => state.payeeSlice.payeeRecommendedDetails;

export default payeeSlice.reducer;